import { webAudioContext } from "./webaudio_context";
import { webAudioUtils } from "./webaudio_utils";
import { NodeHtml } from "./node_html";

class NodePanner3dHtml extends NodeHtml {
  handlers = this.extendParentHandlers({
    distance: { handler: this.updatePannerNodePosition, type: "float", default: 5, min: 0, max: 300 },
    angle: { handler: this.updatePannerNodePosition, type: "float", default: 0 },
    volume: { handler: this.setVolume, type: "float", default: 0, min: -80, max: 12 },
  });

  setProps(dnode) {
    dnode.output = webAudioContext.context.createGain();
    dnode.output.gain.value = webAudioUtils.dbToJs(dnode.props.volume);

    dnode.listener = this.#createListener(webAudioContext.context);
    dnode.input = this.#createPannerNode.call(this);
    dnode.input.connect(dnode.output);

    this.updatePannerNodePosition(dnode);
    super.setProps(dnode);
  }

  updatePannerNodePosition(dnode) {
    const { distance, angle } = dnode.props;
    const { positionX, positionY, positionZ } = dnode.input;

    const { posX, posY, posZ } = this.#getPannerPosition(distance, angle);

    if (dnode.input.positionX) {
      positionX.value = posX;
      positionY.value = posY;
      positionZ.value = posZ;
    } else {
      dnode.input.setPosition(posX, posY, posZ);
    }

    const { orientationX, orientationY, orientationZ } =
      this.#getPannerOrientation(angle);

    if (dnode.input.orientationX) {
      dnode.input.orientationX.value = orientationX;
      dnode.input.orientationY.value = orientationY;
      dnode.input.orientationZ.value = orientationZ;
    } else {
      dnode.input.setOrientation(orientationX, orientationY, orientationZ);
    }
  }

  #getPannerOrientation(angle) {
    const yRotationToVector = (degrees) => {
      // convert degrees to radians and offset the angle so 0 points towards the listener
      const radians = degrees * (Math.PI / 180);
      // using cosine and sine here ensures the output values are always normalised
      // i.e. they range between -1 and 1
      const x = Math.cos(radians);
      const z = Math.sin(radians);

      // we hard-code the Y component to 0, as Y is the axis of rotation
      return { orientationX: x, orientationY: 0, orientationZ: z };
    };

    const angleToListener = (180 + angle + 90) % 360;
    return yRotationToVector(angleToListener);
  }

  #createListener(webaudio_context) {
    const listener = webaudio_context.listener;
    const posX = window.innerWidth / 2;
    const posY = 0;
    const posZ = 0;

    if (listener.positionX) {
      listener.positionX.value = posX;
      listener.positionY.value = posY;
      listener.positionZ.value = posZ;
    } else {
      listener.setPosition(posX, posY, posZ);
    }

    if (listener.forwardX) {
      // pointing head forward (inside the screen ↑)
      listener.forwardX.value = 0;
      listener.forwardY.value = 0;
      listener.forwardZ.value = -1;

      // head on the ground level
      listener.upX.value = 0;
      listener.upY.value = 1;
      listener.upZ.value = 0;
    } else {
      listener.setOrientation(0, 0, -1, 0, 1, 0);
    }

    return listener;
  }

  #createPannerNode() {
    const pannerNode = webAudioContext.context.createPanner();
    pannerNode.panningModel = "HRTF";
    pannerNode.distanceModel = "linear";
    pannerNode.maxDistance = 300;
    pannerNode.refDistance = 1;
    pannerNode.rolloffFactor = 1;
    pannerNode.coneInnerAngle = 360;
    pannerNode.coneOuterAngle = 0;
    pannerNode.coneOuterGain = 0;
    return pannerNode;
  }

  #getPannerPosition(distance, angle) {
    // distance and ange correspond to radius and angle in polar coordinates system

    const unitCircleAngle = angle >= 0 ? angle : 360 - Math.abs(angle);
    let angleRad = unitCircleAngle * (Math.PI / 180);
    angleRad += Math.PI / 2; // offset given angle with 90°, because user pointing forward at 90° to the baseline
    angleRad %= 2 * Math.PI;

    let posX = distance * Math.cos(angleRad);
    posX += window.innerWidth / 2; // to align X-axis value with listener's position

    const posY = 0;
    let posZ = distance * Math.sin(angleRad);
    posZ *= -1; // negative values are inside the screen

    return { posX, posY, posZ };
  }

  setVolume(dnode) {
    dnode.output.gain.value = webAudioUtils.dbToJs(dnode.props.volume);
  }
}

const nodePanner3dHtml = new NodePanner3dHtml("webaudio:node:panner_3d");
export { nodePanner3dHtml, NodePanner3dHtml };
